/**
 * usePageInspector — the inspector / section-selection sub-system of PageRenderer.
 *
 * Owns inspectMode + selectedSections state, the toggleInspect handler, the
 * iframe<->parent postMessage listener for section select/deselect/patch-failed,
 * and the external-event effects (xavior:inspect-off / xavior:deselect-section /
 * xavior:inspect-toggle, plus inspect-state reset on page switch).
 *
 * Ref-coupling (one-way): the orchestrator OWNS iframeRef / renderedHtmlRef /
 * patchNonceRef and the setForceFullReload setter; this hook receives them as
 * params and never re-creates them. NEVER imports PageRenderer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { writeInspectorPreference } from '../lib/inspector-preference';
import type { SectionInfo } from '../types';

interface UsePageInspectorArgs {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  renderedHtmlRef: React.MutableRefObject<string | null>;
  patchNonceRef: React.MutableRefObject<string>;
  conversationId: string | undefined;
  objectId: string | undefined;
  liveKey: string;
  setForceFullReload: React.Dispatch<React.SetStateAction<number>>;
}

export function usePageInspector({
  iframeRef,
  renderedHtmlRef,
  patchNonceRef,
  conversationId,
  objectId,
  liveKey,
  setForceFullReload,
}: UsePageInspectorArgs) {
  // Inspect (section-click-to-select) mode is OFF by default; the user turns
  // it on explicitly via the Edit button in the page header. Previous versions
  // auto-activated from a per-conversation localStorage preference on mount,
  // which meant sections would become clickable silently. We now require an
  // explicit toggle so the interaction is discoverable.
  const [inspectMode, setInspectMode] = useState<boolean>(false);
  // The selected-sections value is never read in render — the badge strip was
  // removed in favour of the chat composer's FocusChipRow. We keep the state
  // machinery (the functional setter drives the `xavior:edit-sections-changed`
  // dispatches the composer listens to), but only destructure the setter so
  // strict `noUnusedLocals` stays clean.
  const [, setSelectedSections] = useState<SectionInfo[]>([]);

  // Listen for section selection messages from iframe.
  // Sandboxed iframes (allow-scripts, no allow-same-origin) post with
  // origin 'null'. The origin check alone is not sufficient — ANY other
  // sandboxed iframe on the page produces 'null' too. We additionally
  // gate on `e.source === iframeRef.current?.contentWindow` so a rogue
  // sibling iframe cannot spam crafted section IDs or forge a
  // `xavior-patch-failed` message that would force a full srcDoc reload
  // (the very flash bug the patch pipeline was built to avoid).
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== 'null') return; // only accept from sandboxed iframes
      if (e.source !== iframeRef.current?.contentWindow) return; // only THIS iframe
      if (!e.data || typeof e.data.type !== 'string') return;
      if (e.data.type === 'xavior-section-selected') {
        setSelectedSections(prev => {
          const next = [...prev, e.data.section as SectionInfo];
          // Map .id → .sectionId for ChatBox which expects {sectionId, label}
          window.dispatchEvent(new CustomEvent('xavior:edit-sections-changed', {
            detail: { sections: next.map((s: SectionInfo) => ({ sectionId: s.id, label: s.label })) },
          }));
          return next;
        });
      } else if (e.data.type === 'xavior-section-deselected') {
        setSelectedSections(prev => {
          const next = prev.filter(s => s.id !== (e.data.section as SectionInfo).id);
          window.dispatchEvent(new CustomEvent('xavior:edit-sections-changed', {
            detail: { sections: next.map((s: SectionInfo) => ({ sectionId: s.id, label: s.label })) },
          }));
          return next;
        });
      } else if (e.data.type === 'xavior-patch-failed') {
        // Iframe-side applier reported a failure (section missing, DOM
        // exception, etc.). This message drives a full srcDoc reassign
        // (DoS vector if forged), so in addition to the source gate
        // above we require the nonce — only the inspector script we
        // injected knows N, so a rogue iframe cannot trigger reloads.
        if (e.data.n !== patchNonceRef.current) return;
        console.warn('[PageRenderer] Patch failed — falling back to full reload:', e.data.reason, 'section:', e.data.sectionId);
        renderedHtmlRef.current = null; // force srcDoc path on next render
        setForceFullReload(n => n + 1);
        // Unify with the app-layer failure channel. `recovered: true` — the
        // full-reload fallback above re-renders from the latest html, so the
        // page self-heals; the global toast skips recovered events and only
        // surfaces failures nothing repaired.
        window.dispatchEvent(new CustomEvent('xavior:patch-failed', {
          detail: { moduleId: liveKey, sectionId: e.data.sectionId, reason: e.data.reason ?? 'dom-apply-failed', recovered: true },
        }));
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Listen for external inspect-off signal (ChatBox clears pills on page
  // switch or after sending). Forwards to iframe via postMessage.
  useEffect(() => {
    const handler = () => {
      iframeRef.current?.contentWindow?.postMessage('xavior-inspect-off', '*');
      setInspectMode(false);
      setSelectedSections([]);
    };
    window.addEventListener('xavior:inspect-off', handler as EventListener);
    return () => window.removeEventListener('xavior:inspect-off', handler as EventListener);
  }, []);

  const toggleInspect = useCallback(() => {
    const next = !inspectMode;
    setInspectMode(next);
    writeInspectorPreference(conversationId, next);
    if (!next) {
      setSelectedSections([]);
      window.dispatchEvent(new CustomEvent('xavior:edit-sections-changed', { detail: { sections: [] } }));
    }
    // Broadcast the new inspect state so the parent's Edit button can show
    // an active visual (the button can't read this component's state
    // directly — they're sibling subtrees).
    window.dispatchEvent(new CustomEvent('xavior:inspect-state-changed', {
      detail: { active: next, pageKey: objectId },
    }));
    iframeRef.current?.contentWindow?.postMessage(
      next ? 'xavior-inspect-on' : 'xavior-inspect-off', '*'
    );
  }, [inspectMode, conversationId, objectId]);

  // Listen for external section-deselect events from ChatBox (chip × or
  // "Clear all"). Forward to the iframe so the highlight updates without
  // requiring the user to click through the inspector again.
  useEffect(() => {
    const onDeselect = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail.sectionId === 'string') {
        setSelectedSections(prev => prev.filter(s => s.id !== detail.sectionId));
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'xavior-inspect-deselect', sectionId: detail.sectionId },
          '*',
        );
      } else {
        // No sectionId → clear-all
        setSelectedSections([]);
        iframeRef.current?.contentWindow?.postMessage('xavior-inspect-clear', '*');
      }
    };
    window.addEventListener('xavior:deselect-section', onDeselect as EventListener);
    return () => window.removeEventListener('xavior:deselect-section', onDeselect as EventListener);
  }, []);

  // Ref kept for the onLoad handler's compatibility path (it used to forward
  // the auto-activate signal). Inspect now starts OFF on every page switch.
  const autoActivateOnLoadRef = useRef<boolean>(false);
  useEffect(() => {
    // Reset inspect state on every page switch so changing pages doesn't
    // carry over an accidentally-on state from the previous page.
    setInspectMode(false);
    setSelectedSections([]);
    // Tell the parent's Edit button to drop its active visual on page switch.
    window.dispatchEvent(new CustomEvent('xavior:inspect-state-changed', {
      detail: { active: false, pageKey: objectId },
    }));
  }, [conversationId, liveKey, objectId]);

  // External toggle from any out-of-tree caller (legacy: the page header
  // had an Edit button that dispatched this; the button has since moved
  // INTO the share-bar below and calls toggleInspect directly, but we
  // keep the listener as a defensive integration point in case the chat
  // layer or a deeplink wants to flip inspect mode).
  useEffect(() => {
    const handler = () => toggleInspect();
    window.addEventListener('xavior:inspect-toggle', handler as EventListener);
    return () => window.removeEventListener('xavior:inspect-toggle', handler as EventListener);
  }, [toggleInspect]);

  return { inspectMode, toggleInspect, autoActivateOnLoadRef };
}
